import { useState, useCallback, useMemo } from "react";
import { useMutation } from "urql";
import { useBeforeUnload, useNavigate } from "react-router-dom";
import Page from "../Page";
import {
  ArticleType,
  PatternTable_ArticleFragmentDoc,
} from "../../__generated__/gql/graphql";
import type { FragmentType } from "../../__generated__/gql";
import { getFragmentData, graphql } from "../../__generated__/gql";
// import Stepper from "../../ui/Stepper/Stepper";
import { CheckCircle } from "../../ui/Icon/Icon";
import urls from "../../routes";
// import styles from "./NewPattern.module.css";
import ConfigureSymbols from "./steps/ConfigureSymbols/ConfigureSymbols";
import type { ConfigureSymbolsFormData } from "./steps/ConfigureSymbols/ConfigureSymbols";
import CreatePattern from "./steps/CreatePattern/CreatePattern";
import type {
  CreatePatternData,
  CreatePatternStep,
} from "./steps/CreatePattern/CreatePattern";
import KeywordReview from "./steps/KeywordReview/KeywordReview";
import { useToast } from "../../ui/Toast/ToastContext";
import UnsavedChangesModal from "../../components/UnsavedChangesModal/UnsavedChangesModal";
import { useDisclosure } from "../../ui/util/useDisclosure";
import ConfirmKeywordsModal from "./ConfirmKeywordsModal";

const CreatePatternMutation = graphql(/* GraphQL */ `
  mutation createPattern($input: CreatePatternInput!) {
    createPattern(input: $input) {
      id
      templateNodes {
        type
        value
      }
      patternSymbols {
        id
        name
        color
        values
      }
      articles {
        ...PatternTable_Article
      }
      status
      type
    }
  }
`);

const DeletePatternMutation = graphql(/* GraphQL */ `
  mutation deletePattern($id: String!) {
    deletePattern(id: $id) {
      id
    }
  }
`);

const GenerateNewPatternArticlesMutation = graphql(/* GraphQL */ `
  mutation generateNewPatternArticles($input: GenerateArticlesInput!) {
    generateArticles(input: $input) {
      ... on GenerateArticlesResult {
        count
      }
      ... on InsufficientCreditsError {
        __typename
        message
      }
    }
  }
`);

const DeleteNongeneratedArticlesMutation = graphql(/* GraphQL */ `
  mutation deleteNongeneratedArticles($input: DeleteArticlesInput!) {
    deleteArticles(input: $input) {
      count
    }
  }
`);

type Step = "CreatePattern" | "ConfigureSymbols" | "KeywordReview";

type NewPatternData = CreatePatternData;

function NewPatternSteps({
  articles,
  step,
  createPatternStep,
  setCreatePatternStep,
  data,
  setData,
  onEditPattern,
  onSubmitPattern,
  onSubmitCreatePattern,
  setSelectedArticles,
}: {
  articles?: FragmentType<typeof PatternTable_ArticleFragmentDoc>[] | null;
  step: Step;
  createPatternStep: CreatePatternStep;
  setCreatePatternStep: React.Dispatch<React.SetStateAction<CreatePatternStep>>;
  data: NewPatternData;
  setData: React.Dispatch<React.SetStateAction<NewPatternData>>;
  onEditPattern: () => void;
  onSubmitPattern: () => void;
  onSubmitCreatePattern: (data: CreatePatternData) => void;
  setSelectedArticles: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const onNextStep = useCallback(
    (newData: Partial<NewPatternData>) => {
      setData((prev) => ({
        ...prev,
        ...newData,
      }));
    },
    [setData],
  );

  const onConfigureSymbolsChange = useCallback(
    (configureSymbolsFormData: ConfigureSymbolsFormData) => {
      setData((prev) => ({
        ...prev,
        symbols: configureSymbolsFormData.symbols.map((symbol) => ({
          ...symbol,
          values: symbol.values.map((value) => value.value),
        })),
      }));
    },
    [setData],
  );

  if (step === "CreatePattern") {
    return (
      <CreatePattern
        defaultData={data}
        onEditPattern={onEditPattern}
        onNextStep={onNextStep}
        onSubmit={onSubmitCreatePattern}
        setStep={setCreatePatternStep}
        step={createPatternStep}
      />
    );
  }

  if (step === "ConfigureSymbols") {
    return (
      <ConfigureSymbols
        onSubmit={onSubmitPattern}
        onChange={onConfigureSymbolsChange}
        onEditPattern={onEditPattern}
        symbols={data.symbols}
        templateNodes={data.templateNodes}
        type={data.type}
      />
    );
  }

  if (step === "KeywordReview" && articles) {
    return (
      <KeywordReview
        articles={articles}
        setSelectedArticles={setSelectedArticles}
      />
    );
  }

  return null;
}

function NewPattern() {
  const { showToast } = useToast();
  const [confirmKeywordsModalOpened, confirmKeywordsModal] = useDisclosure();
  const [step, setStep] = useState<Step>("CreatePattern");
  const [createPatternStep, setCreatePatternStep] =
    useState<CreatePatternStep>("ConfigureTemplate");
  const [data, setData] = useState<NewPatternData>({
    templateNodes: [],
    symbols: [],
    type: ArticleType.Overview,
    titleType: "keyword",
  });
  const [selectedArticles, setSelectedArticles] = useState<string[]>([]);
  const navigate = useNavigate();
  // const steps: Step[] = ["CreatePattern", "ConfigureSymbols", "KeywordReview"];
  const [, deletePattern] = useMutation(DeletePatternMutation);
  const [createPatternResponse, createPattern] = useMutation(
    CreatePatternMutation,
  );

  const title = useMemo(() => {
    if (step === "CreatePattern") {
      return "New Pattern";
    }

    if (step === "ConfigureSymbols") {
      return "Configure Symbols";
    }

    return "Keyword Review";
  }, [step]);

  const onEditPattern = useCallback(() => {
    setCreatePatternStep("ConfigureTemplate");
    setStep("CreatePattern");
  }, []);

  const onBack = useCallback(async () => {
    if (step === "CreatePattern") {
      if (createPatternStep === "ConfigureType") {
        setCreatePatternStep("ConfigureTemplate");
      } else if (createPatternStep === "ConfigureTitle") {
        setCreatePatternStep("ConfigureType");
      }
    } else if (step === "KeywordReview") {
      if (createPatternResponse.data?.createPattern.id) {
        // Don't wait for this to complete, just do it in the background
        await deletePattern({
          id: createPatternResponse.data?.createPattern.id,
        });
      }
      setStep("ConfigureSymbols");
      setCreatePatternStep("ConfigureTemplate");
      // setCreatePatternStep("ConfigureTitle");
    } else if (step === "ConfigureSymbols") {
      setStep("CreatePattern");
    }
  }, [
    createPatternResponse.data?.createPattern.id,
    createPatternStep,
    deletePattern,
    step,
  ]);

  const [, generateArticles] = useMutation(GenerateNewPatternArticlesMutation);
  const [, deleteArticles] = useMutation(DeleteNongeneratedArticlesMutation);

  const onSubmitCreatePattern = useCallback(
    (createPatternData: CreatePatternData) => {
      setData((previousData) => ({
        ...createPatternData,
        symbols: createPatternData.symbols.map((symbol) => ({
          ...symbol,
          values:
            previousData.symbols.find((s) => s.name === symbol.name)?.values ??
            [],
        })),
      }));
      setStep("ConfigureSymbols");
    },
    [setData, setStep],
  );

  const onSubmitPattern = useCallback(async () => {
    await createPattern({
      input: {
        templateNodes: data.templateNodes,
        patternSymbols: data.symbols.map((symbol) => ({
          ...symbol,
          values: symbol.values,
        })),
        type: ArticleType.Overview,
        // type: data.type,
      },
    });
    setStep("KeywordReview");
  }, [createPattern, data.symbols, data.templateNodes]);

  const onSubmitKeywordReview = useCallback(
    async (data: { status: "generate" | "draft" }) => {
      const deletedArticles = createPatternResponse.data?.createPattern.articles
        ?.map(
          (article) =>
            getFragmentData(PatternTable_ArticleFragmentDoc, article).id,
        )
        .filter((id) => !selectedArticles.includes(id));

      if (data.status === "generate") {
        const [generateArticlesResponse] = await Promise.all([
          generateArticles({
            input: {
              filters: [
                {
                  ids: selectedArticles,
                },
              ],
            },
          }),
          ...(deletedArticles && deletedArticles?.length > 0
            ? [
                deleteArticles({
                  input: {
                    filters: [
                      {
                        ids: deletedArticles,
                      },
                    ],
                  },
                }),
              ]
            : []),
        ]);

        if (
          generateArticlesResponse.data?.generateArticles.__typename ===
          "InsufficientCreditsError"
        ) {
          showToast({
            variant: "error",
            title: "You've run out of article credits",
          });
        }
      } else if (deletedArticles?.length) {
        await deleteArticles({
          input: {
            filters: [
              {
                ids: deletedArticles,
              },
            ],
          },
        });
      }
      await navigate(urls.patterns, { state: { ignoreBlocker: true } });
    },
    [
      createPatternResponse.data?.createPattern.articles,
      deleteArticles,
      generateArticles,
      navigate,
      selectedArticles,
      showToast,
    ],
  );

  const headerButtons = useMemo(() => {
    if (step === "KeywordReview") {
      return [
        {
          type: "button",
          variant: "primary",
          leftIcon: <CheckCircle size="medium" />,
          children: "Accept Selection & Continue",
          onClick: confirmKeywordsModal.open,
        } as const,
      ];
    }

    return [];
  }, [confirmKeywordsModal.open, step]);

  useBeforeUnload(
    useCallback(async () => {
      if (createPatternResponse.data) {
        await deletePattern({
          id: createPatternResponse.data.createPattern.id,
        });
      }
    }, [createPatternResponse.data, deletePattern]),
  );

  return (
    <Page
      buttons={headerButtons}
      fullHeight
      onBack={
        !(step === "CreatePattern" && createPatternStep === "ConfigureTemplate")
          ? onBack
          : undefined
      }
      title={title}
      documentTitle={title}
    >
      <UnsavedChangesModal
        isDirty={!!data.templateNodes.length}
        onDiscard={async () => {
          if (createPatternResponse.data) {
            await deletePattern({
              id: createPatternResponse.data.createPattern.id,
            });
          }
        }}
      />
      <NewPatternSteps
        articles={createPatternResponse.data?.createPattern.articles}
        createPatternStep={createPatternStep}
        data={data}
        onEditPattern={onEditPattern}
        onSubmitCreatePattern={onSubmitCreatePattern}
        onSubmitPattern={onSubmitPattern}
        setCreatePatternStep={setCreatePatternStep}
        setData={setData}
        setSelectedArticles={setSelectedArticles}
        step={step}
      />
      <ConfirmKeywordsModal
        isOpen={confirmKeywordsModalOpened}
        onClose={confirmKeywordsModal.close}
        onSubmit={onSubmitKeywordReview}
        keywordsCount={selectedArticles.length}
      />
      {/* <Stepper
        className={styles.stepper}
        currentStep={steps.indexOf(step)}
        steps={["Create Pattern", "Setup Symbols", "Review & Publish"]}
      /> */}
    </Page>
  );
}

export default NewPattern;
